import React from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { Field } from 'formik'

import { t } from 'translations'
import { DateTimePicker, DateTypes } from 'components/UI'
import { Button as MerinioButton } from 'ui'
import { Format } from 'services/Dates'
import { getFieldState } from 'services/Forms'

const wrapInputGroup = (input, inputAppend) => {
  if (!inputAppend.length) return input
  return (
    <div className="input-group">
      {input}
      <div className="input-group-append">
        {inputAppend.map((append, index) => (
          <React.Fragment key={index}>{append}</React.Fragment>
        ))}
      </div>
    </div>
  )
}

const outFormats = {
  time: 'serverTime',
  timeStandard: 'serverTime',
  date: 'date',
  dateTime: 'server',
  dateTimeStandard: 'server',
  dateMultiple: 'date'
}

const DateTime = ({ customLabelElement, fieldClass, type, inputAppend, inputGroup, isClearable, dontFormat, handleChange, descriptionVariables, ...props }) => {
  const { standardTimeDateDisplay } = useSelector(state => state.settings)

  // Add the required * if the input is mandatory
  return (
    <Field {...props}>
      {({ field, form, meta }) => {
        const fieldState = getFieldState(form, field)

        // Allows usage of pre-defined picker types
        if (typeof type === 'string' && typeof DateTypes[type] !== 'undefined') {
          if (['dateTime', 'time'].includes(type) && standardTimeDateDisplay) type += 'Standard'
          props.options = { ...DateTypes[type], ...(props.options || {}) }
        }
        const isMultiple = ['multiple', 'range'].includes((props.options || {}).mode)

        // Prevent showing placeholder if translation does not exist.
        // Avoids issues on mobile where untranslated placeholders can be shown.
        let placeholderString = ''
        if (typeof props.text.placeholder !== 'string') {
          const placeholderKey = `${props.module}.${(props.translation || props.name).toUpperCase()}.PLACEHOLDER`
          const placeholderTranslation = t(placeholderKey)
          if (placeholderKey !== placeholderTranslation) placeholderString = placeholderTranslation
        } else placeholderString = props.text.placeholder

        return (
          <div
            className={`form-group ${fieldState} ${props.required ? 'required' : ''}${props.formGroupStyle ? ` ${props.formGroupStyle}` : ''} ${fieldClass}`}
          >
            {!props.hideLabel && (
              <label className="control-label">
                {props.text.label || t(`${props.module}.${(props.translation || props.name).toUpperCase()}.LABEL`)}
                {customLabelElement}
              </label>
            )}
            {wrapInputGroup(
              <DateTimePicker
                {...props}
                {...field}
                notStatic={true}
                fieldState={fieldState}
                placeholder={placeholderString}
                onChange={value => {
                  if (!value[0]) {
                    form.setFieldValue(props.name, isMultiple ? [] : '')
                  } else {
                    const formattedValues = value.map(aValue =>
                      typeof outFormats[type] !== 'undefined' && !dontFormat ? Format(aValue, { outFormat: outFormats[type] }) : aValue
                    )
                    form.setFieldValue(props.name, isMultiple ? formattedValues : formattedValues[0])
                  }
                  if (handleChange) handleChange(value)
                }}
              />,
              [
                inputAppend,
                isClearable ? (
                  <MerinioButton type="button" color="secondary" onClick={() => form.setFieldValue(props.name, isMultiple ? [] : '')} outlined>
                    <i className="fas fa-trash"></i>
                  </MerinioButton>
                ) : null
              ].filter(Boolean)
            )}
            {!props.hideDescription && (
              <small className="form-text text-muted">
                {props.text.description ||
                  t(
                    `${props.module}.${(props.translation || props.name).toUpperCase()}.${props.descriptionKey ? props.descriptionKey : 'DESCRIPTION'}`,
                    descriptionVariables
                  )}
              </small>
            )}
            {meta.error ? <span className="text-danger">{t(meta.error)}</span> : null}
          </div>
        )
      }}
    </Field>
  )
}

export default DateTime

// Set the proptypes for validation
DateTime.propTypes = {
  module: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  type: PropTypes.string,
  options: PropTypes.object,
  descriptionKey: PropTypes.string,
  hideLabel: PropTypes.bool,
  hideDescription: PropTypes.bool,
  formGroupStyle: PropTypes.string,
  text: PropTypes.object,
  translation: PropTypes.string,
  descriptionVariables: PropTypes.object,
  customLabelElement: PropTypes.element,
  handleChangeFormattedValue: PropTypes.func
}
DateTime.defaultProps = {
  required: false,
  text: {}
}
